import { type PowerSyncDrizzleDB, user } from "@jornada/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * ── Why ALL mutations below go through `patchRowVerified` ───────────────────
 *
 * Four of them (`locale`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`) had this shape:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * that is, in guest mode the UPDATE came out **without `WHERE`** → it rewrote ALL
 * the `user` rows of the local database. The branch existed by symmetry with
 * `getUserThemeSettingsQuery`, which for the guest reads with `.limit(1)` without a
 * filter; but a read `limit(1)` and an UPDATE without a filter are not the same
 * bet: the first picks the wrong row, the second crushes them all.
 *
 * The premise behind it ("the guest database has only one
 * `user` row") is false in a specific window: during the guest→real upgrade, the
 * guest database CONNECTS to the real account's connector and
 * self-subscribes to all streams—including `organizations`, which syncs
 * one `user` row for EACH co-member of that account's organizations.
 * There the table has 1 + 1 + N rows, `user` is NOT `localOnly`, and each touched
 * row additionally enqueues its own op in `ps_crud`. `patchRowVerified` always
 * builds its own `WHERE eq(id, userId)` — there is no way to invoke it
 * without a filter, so that insecure branch is structurally unrepeatable.
 *
 * ── And why the `WHERE` alone is NOT enough (§4.11 of `infra/PENDING_WORK.md`) ──
 * `user` was audited as a candidate for exclusion (¹the row is seeded by the server
 * upon registration²) and the premise was false here just as in the other 11 repos
 * of the campaign: `ThemeProvider` (`apps/web/src/providers/theme-provider.tsx`)
 * seeded the LOCAL row with `db.insert(user).values({ id: uid, … })` for
 * ANY `uid`, without distinguishing guest from real account (`if (!db || !uid)
 * return`) — competing with the row that the server already creates unconditionally
 * upon registration. **2026-08-13: closed at origin** (`uid !== VISITOR_ID` in the
 * seeding `createEffect`, see
 * `packages/shared/src/users/__tests__/theme-provider-seed-window.test.ts`) —
 * there are no longer two INSERTs competing for the `user` row of a real account.
 *
 * That does NOT empty this module: `patchRowVerified` still defends a
 * DIFFERENT window independent of seeding — the reconstruction of the PowerSync
 * view from the checkpoint (see `verified-write.ts`), which exists for
 * ANY row, seeded or not, and which the startup guard of `ThemeProvider`
 * changes nothing about. Same mechanism, different domain: in
 * `workforce/mutations.ts`, `approveProposedEntryMutation` prepends
 * `waitForRowPresent` so that a newly generated `proposed_time_entry` is not
 * read as "without assigned project" merely because its checkpoint has not landed yet.
 * Here: if the checkpoint has not yet brought the `user` row that the server already created,
 * a direct `UPDATE … WHERE id = ?` against it matches ZERO rows and does not fail:
 * the preference that the user just touched (language, theme, avatar…) is
 * lost silently. `patchRowVerified` rereads and retries with backoff before
 * giving up.
 *
 * `options?: VerifiedWriteOptions` is exposed in each mutation ONLY so that
 * tests can inject a short `backoffMs` (`patchRowVerified` defaults
 * to ≈6 s to exhaust) — production callers never pass it.
 *
 * Guarded by `packages/shared/src/users/__tests__/mutations-where.test.ts` and
 * by `packages/shared/src/powersync/__tests__/verified-write.test.ts`.
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale }, options);
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId }, options);
}

/**
 * Writes the avatar KEY to the local row.
 *
 * It is written local-first (like language, font, and theme) and not through REST: this way
 * the new photo is visible immediately and survives a cold reload without
 * a connection. The BINARY does go through REST (`POST /api/avatar`) because it does not fit in the
 * sync queue; what travels here is only the key returned by that upload.
 *
 * `null` = "remove the photo" (the initials return).
 *
 * The visitor has no own synchronizable row and cannot have an avatar (zero rule),
 * so there is no `VISITOR_ID` branch here as in `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey }, options);
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId }, options);
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 }, options);
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    user,
    user.id,
    userId,
    { follow_device_theme: followDevice ? 1 : 0 },
    options,
  );
}
